import * as React from 'react';
import styled from 'styled-components';

import { AquaTheme as T } from '../AquaTheme';

interface IProps {
  /** @ignore */
  className?: string;
  /** Current map latitude. */
  latitude: number;
  /** Current map zoom. */
  zoom: number;
  /**
   * Maximum width of the scale bar, in pixels.
   * @default 100
   */
  width?: number;
}

// Display steps, in meters:
const STEPS = [
  1, 2, 3, 5, 10, 20, 30, 50, 100, 200, 300, 500, 1000, 2000, 3000, 5000, 10000,
  20000, 30000, 50000, 100000, 200000, 300000, 500000, 1000000, 2000000,
  3000000, 5000000
];

const formatDistance = (meters: number) => meters >= 1000
  ? `${(meters / 1000).toLocaleString()} km`
  : `${meters.toLocaleString()} m`;

/**
 * Scale bar, using the same math as AquaUI's ScaleControl, drawn as a
 * hairline ruler so it can sit on top of a GlassPane stack.
 */
const AquaScaleBase = (props: IProps) => {
  const maxWidth = props.width ?? 100;
  const metersPerPixel = 156543.03392 * Math.cos(props.latitude * Math.PI / 180) / Math.pow(2, props.zoom + 1);
  const meters = maxWidth * metersPerPixel;
  // Largest step that fits:
  const dist = STEPS.filter(s => s <= meters).pop() ?? STEPS[0];
  const width = maxWidth * dist / meters;

  return (
    <div className={props.className}>
      <Bar style={{ width: `${width}px` }}/>
      <Label>{formatDistance(dist)}</Label>
    </div>
  );
}

const Bar = styled.div`
  height: 6px;
  box-sizing: border-box;
  border: solid 2px ${T.colors.neutral[100]};
  border-top: none;
  filter: drop-shadow(0 1px 1px rgba(0,0,0,0.6));
  transition: width ease-in-out ${T.duration}ms;
`

const Label = styled.div`
  font: ${T.font.labelSmall};
  color: ${T.colors.neutral[100]};
  text-shadow: 0 1px 2px rgba(0,0,0,0.8);
  white-space: nowrap;
`

const AquaScale = styled(AquaScaleBase)`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 8px;
  user-select: none;
  pointer-events: none;
`

export { AquaScale }
